import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight, Info } from 'lucide-react';
import { haptic } from '@/shared/utils/telegram';
import { useFilterStore } from '@/features/filters/store';
import { useBrandsControllerGetBrands } from '@/generated/api/brands/brands';
import { PageHeader } from '@/shared/ui';
import { castToEnrichedBrand } from '@/shared/api/registry-api';

export function BrandsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { brands: selectedBrands, setBrands } = useFilterStore();

  // Fetch brands from API
  const brandsQuery = useBrandsControllerGetBrands();
  const { data, isLoading, error } = brandsQuery;

  // GET /brands already returns registry enrichment (id/slug/displayName/program/platform/
  // status/identifiers) -- the generated BrandDto type just doesn't declare it yet (stale
  // Swagger snapshot, see shared/api/registry-api.ts). Cast rather than refetch.
  const enrichedBrands = useMemo(() => (data?.data?.brands ?? []).map(castToEnrichedBrand), [data]);

  // Filter brands by search query -- matches on the raw brand text (what's actually sent as
  // ?brand=) and, when present, the registry display name (bitzbet/BlitzBet-style divergence).
  const filteredBrands = useMemo(() => {
    if (!searchQuery) return enrichedBrands;
    const q = searchQuery.toLowerCase();
    return enrichedBrands.filter(
      (brand) =>
        brand.brand.toLowerCase().includes(q) ||
        (brand.displayName?.toLowerCase().includes(q) ?? false),
    );
  }, [enrichedBrands, searchQuery]);

  const handleBrandClick = (brandName: string) => {
    haptic.impact('light');
    // Set selected brand and navigate to events page
    setBrands([brandName]);
    navigate('/events');
  };

  const handleDetailsClick = (e: React.MouseEvent, brand: (typeof filteredBrands)[number]) => {
    e.stopPropagation();
    haptic.impact('light');
    navigate(`/brands/${encodeURIComponent(brand.slug ?? brand.brand)}`);
  };

  // Render loading state
  const loadingState = isLoading ? (
    <div className="flex items-center justify-center py-12">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--tg-theme-link-color)] border-t-transparent" />
        <p className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase">
          Loading brands...
        </p>
      </div>
    </div>
  ) : null;

  // Render error state
  const errorState = error ? (
    <div className="p-6 mx-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-center">
      <p className="text-sm font-bold text-rose-600">Failed to load brands</p>
      <p className="text-xs text-rose-500/80 mt-1">Please try again later</p>
    </div>
  ) : null;

  return (
    <div className="flex flex-col h-full animate-in slide-in-from-right duration-300 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[var(--tg-theme-bg-color)] pt-2 pb-4 flex flex-col gap-3">
        <PageHeader title="Brands" />

        {/* Search */}
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Search size={14} className="text-[var(--tg-theme-hint-color)]" />
          </div>
          <input
            type="text"
            placeholder="Search brands..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-11 pl-10 pr-4 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-bold focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20 placeholder:opacity-50"
          />
        </div>
      </div>

      {loadingState}
      {errorState}

      {/* Brands List */}
      {!isLoading && !error && (
        <>
          <div className="px-4 pb-20 space-y-3">
            {filteredBrands.map((brand) => {
              const isSelected = selectedBrands.includes(brand.brand);
              const title = brand.displayName ?? brand.brand;
              // Only show the raw value as a subtitle when it actually diverges from the
              // display name (bitzbet/BlitzBet-style) -- otherwise it would just repeat itself.
              const showRawAlias =
                brand.displayName && brand.displayName.toLowerCase() !== brand.brand.toLowerCase();

              return (
                <div
                  key={brand.brand}
                  role="button"
                  tabIndex={0}
                  className={`w-full p-4 rounded-2xl border transition-all text-left active:scale-[0.98] cursor-pointer ${
                    isSelected
                      ? 'bg-[var(--tg-theme-link-color)]/10 border-[var(--tg-theme-link-color)]/20'
                      : 'bg-[var(--tg-theme-secondary-bg-color)] border-black/5 dark:border-white/5'
                  }`}
                  onClick={() => handleBrandClick(brand.brand)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleBrandClick(brand.brand);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-12 h-12 flex-shrink-0 rounded-full bg-[var(--tg-theme-link-color)]/10 flex items-center justify-center">
                        <span className="text-lg font-black text-[var(--tg-theme-link-color)]">
                          {title.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold truncate">{title}</h3>
                        <p className="text-xs text-[var(--tg-theme-hint-color)] font-medium truncate">
                          {showRawAlias ? `${brand.brand} · ` : ''}
                          {brand.totalEvents.toLocaleString()} events
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {brand.lastEventAt && (
                        <span className="text-xs text-[var(--tg-theme-hint-color)]">
                          {getTimeAgo(brand.lastEventAt)}
                        </span>
                      )}
                      <button
                        type="button"
                        aria-label="Brand details"
                        onClick={(e) => handleDetailsClick(e, brand)}
                        className="p-1.5 -m-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 active:scale-90 transition-all"
                      >
                        <Info size={16} className="text-[var(--tg-theme-hint-color)]" />
                      </button>
                      <ChevronRight size={18} className="text-[var(--tg-theme-hint-color)]" />
                    </div>
                  </div>

                  {/* Registry enrichment -- neutral styling either way. A brand with no
                      registry row yet is normal (transitional period), not an error. */}
                  {brand.id ? (
                    <div className="flex flex-wrap items-center gap-1.5 mt-3">
                      {brand.program && (
                        <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full bg-[var(--tg-theme-link-color)]/10 text-[var(--tg-theme-link-color)]">
                          {brand.program}
                        </span>
                      )}
                      {brand.platform && (
                        <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full bg-black/5 dark:bg-white/10 text-[var(--tg-theme-hint-color)]">
                          {brand.platform}
                        </span>
                      )}
                      {brand.status && brand.status !== 'active' && (
                        <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full bg-amber-500/10 text-amber-600">
                          {brand.status}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-[10px] font-medium text-[var(--tg-theme-hint-color)] opacity-60 mt-3">
                      Not in the registry yet
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {filteredBrands.length === 0 && !isLoading && (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm font-bold text-[var(--tg-theme-hint-color)] uppercase">
                No brands found
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
